import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { apiErrorMessage } from '../../utils/gameSchedule';
import { ReactComponent as SquadMark } from '../../assets/icons/squad-mark.svg';

const COVER_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

function Dialog({ label, onClose, children }) {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="profile-modal" role="presentation" onClick={onClose}>
      <div className="profile-dialog" role="dialog" aria-modal="true" aria-label={label} onClick={(event) => event.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

// Start a squad: a name and an optional cover photo.
function CreateDialog({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  const pick = (event) => {
    const next = event.target.files && event.target.files[0];
    if (!next) return;
    if (!COVER_TYPES.includes(next.type)) {
      setError('Choose a JPG, PNG, WebP or HEIC photo.');
      return;
    }
    setError('');
    setFile(next);
    setPreview(URL.createObjectURL(next));
  };

  const submit = async (event) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    setError('');
    try {
      const squad = await apiService.createSquad({ name: trimmed, coverFile: file });
      onCreated(squad);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create this squad.'));
      setSaving(false);
    }
  };

  return (
    <Dialog label="Create a squad" onClose={onClose}>
      <h2>Create a squad</h2>
      <form onSubmit={submit}>
        <label className="profile-photo-pick">
          {preview ? <img src={preview} alt="" /> : <span>Add a photo</span>}
          <input type="file" accept={COVER_TYPES.join(',')} onChange={pick} />
        </label>
        <label className="profile-field">
          <span>Name</span>
          <input type="text" value={name} onChange={(event) => setName(event.target.value)} placeholder="Name your squad" maxLength={80} autoFocus />
        </label>
        {error ? <p className="profile-dialog-error">{error}</p> : null}
        <div className="profile-dialog-actions">
          <button type="button" className="profile-btn is-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="profile-btn is-primary" disabled={saving || !name.trim()}>
            {saving ? 'Creating…' : 'Create squad'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function JoinDialog({ onClose, onJoined }) {
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed || joining) return;
    setJoining(true);
    setError('');
    try {
      const squad = await apiService.joinSquad(trimmed);
      onJoined(squad);
    } catch (err) {
      setError(apiErrorMessage(err, 'That code does not match a squad.'));
      setJoining(false);
    }
  };
  return (
    <Dialog label="Join a squad" onClose={onClose}>
      <h2>Join a squad</h2>
      <form onSubmit={submit}>
        <label className="profile-field">
          <span>Squad code</span>
          <input type="text" value={code} onChange={(event) => setCode(event.target.value)} placeholder="Enter a code" autoFocus autoCapitalize="characters" />
        </label>
        {error ? <p className="profile-dialog-error">{error}</p> : null}
        <div className="profile-dialog-actions">
          <button type="button" className="profile-btn is-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="profile-btn is-primary" disabled={joining || !code.trim()}>
            {joining ? 'Joining…' : 'Join'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// "Your squads" on the profile: the fan's squads as tiles opening their pages, with a
// join-by-code row, or the invitation to start one.
export default function SquadsPanel() {
  const navigate = useNavigate();
  const [squads, setSquads] = useState(null);
  const [failed, setFailed] = useState(false);
  const [dialog, setDialog] = useState(null);
  // The inline code field under the tiles, as the embed's profile has it.
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');

  const load = () => {
    apiService
      .getMySquads()
      .then((payload) => {
        setSquads(Array.isArray(payload?.squads) ? payload.squads : []);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  };

  useEffect(load, []);

  // A new or joined squad opens its page, as the embed does.
  const done = (squad) => {
    setDialog(null);
    if (squad?.id) {
      navigate(`/squads/${encodeURIComponent(String(squad.id))}`);
      return;
    }
    load();
  };

  const joinInline = async (event) => {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed || joining) return;
    setJoining(true);
    setJoinError('');
    try {
      const squad = await apiService.joinSquad(trimmed);
      setCode('');
      done(squad);
    } catch (err) {
      setJoinError(apiErrorMessage(err, 'That code does not match a squad.'));
    } finally {
      setJoining(false);
    }
  };

  return (
    <section className="profile-panel profile-squads">
      <header className="profile-panel-head">
        <h2>Your squads</h2>
        <button type="button" onClick={() => setDialog('create')}>Create squad</button>
      </header>
      {failed ? <p className="profile-panel-empty">Couldn’t load squads.</p> : null}
      {!failed && squads === null ? <p className="profile-panel-empty">Loading squads...</p> : null}
      {!failed && squads && !squads.length ? (
        <div className="profile-squads-empty">
          <span className="profile-squads-mark" aria-hidden="true">
            <SquadMark />
          </span>
          <strong>Squads</strong>
          <p>Team up with friends to earn points and win prizes together.</p>
          <div className="profile-squads-actions">
            <button type="button" className="profile-btn is-primary" onClick={() => setDialog('create')}>Create a squad</button>
            <button type="button" className="profile-btn is-ghost" onClick={() => setDialog('join')}>Join squad</button>
          </div>
        </div>
      ) : null}
      {!failed && squads && squads.length ? (
        <>
          <ul className="profile-squad-tiles">
            {squads.map((squad) => (
              <li key={String(squad.id)}>
                <Link to={`/squads/${encodeURIComponent(String(squad.id))}`}>
                  <span className="profile-squad-cover">
                    {squad.cover_url ? <img src={squad.cover_url} alt="" /> : null}
                  </span>
                  <strong>{squad.name || 'Squad'}</strong>
                </Link>
              </li>
            ))}
          </ul>
          <form className="profile-squads-join" onSubmit={joinInline}>
            <span className="profile-squads-join-label">Have a code?</span>
            <div className="profile-squads-join-row">
              <input
                type="text"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="8-character code"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck="false"
                aria-label="Squad code"
              />
              <button type="submit" disabled={joining || !code.trim()}>Join</button>
            </div>
            <p className="profile-squads-join-error">{joinError}</p>
          </form>
        </>
      ) : null}
      {dialog === 'create' ? <CreateDialog onClose={() => setDialog(null)} onCreated={done} /> : null}
      {dialog === 'join' ? <JoinDialog onClose={() => setDialog(null)} onJoined={done} /> : null}
    </section>
  );
}
